import { useMemo } from 'react'
import styled from 'styled-components'
import { match } from 'ts-pattern'

import { Tooltip } from '@blade/ui-kit'
import { auditToColor } from '@root/entities/pair/ui/constants'

import type { IconProps } from '@blade/ui-kit'
import type { PairAudit } from '@root/entities/pair'
import type { ClassNameProps } from '@root/shared/react'

export type StatIntent = PairAudit.AuditStatus | 'Neutral'

export type PairStatsPanelItemProps = {
  title: React.ReactNode
  icon: React.FC<IconProps>
  value: React.ReactNode
  intent: StatIntent
  tooltip?: React.ReactNode
} & ClassNameProps

export const PairStatsPanelItem: React.FC<PairStatsPanelItemProps> = ({
  title,
  icon,
  value,
  intent,
  tooltip,
  className,
}) => {
  const Icon = icon

  const color = useMemo(() => intentColor(intent), [intent])

  const card = (
    <Root className={className}>
      <Label>{title}</Label>
      <Body style={{ color }}>
        <Icon size={12} />
        <Value>{value}</Value>
      </Body>
    </Root>
  )

  if (tooltip === undefined) {
    return card
  }

  return <Tooltip content={tooltip}>{card}</Tooltip>
}

const intentColor = (intent: StatIntent) =>
  match(intent)
    .with('Neutral', () => 'var(--neutral-9)')
    .otherwise((status) => auditToColor(status))

const Root = styled.div`
  display: grid;
  gap: 3px;
  align-content: center;
  justify-items: center;
`

const Label = styled.div`
  color: var(--text-11);

  text-align: center;

  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`

const Body = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 4px;

  color: var(--stat-color);

  text-align: center;
  font-size: 12px;
  font-weight: 500;
  line-height: 12px;
`

const Value = styled.span`
  white-space: nowrap;
`
